import { Ref } from 'vue'

import { Competition, type Event } from '@collector/sportsapi-client-legacy'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'
import { useEventId } from '@desktop/views/Relation/composables/useEventId'
import { SyncedTimestamp } from '@desktop/views/Relation/utils'
import {
  queryOptions,
  useQuery,
  type UndefinedInitialQueryOptionsWithDataTag,
  type UseQueryReturnType,
} from '@tanstack/vue-query'

export type CompetitionData = {
  competition: Competition & { timestamp: SyncedTimestamp }
}

type CompetitionQueryKey = readonly ['event', 'competition', Ref<number>]

type CompetitionQueryOptions = UndefinedInitialQueryOptionsWithDataTag<
  CompetitionData,
  Error,
  CompetitionData,
  CompetitionQueryKey
>

export const updateCompetitionEvent = (
  competitionData: CompetitionData | undefined,
  eventUpdater: (prevEvent: Event) => Event,
): CompetitionData | undefined => {
  if (!competitionData) return competitionData

  const { competition } = competitionData
  const { season } = competition
  const { stage } = season
  const { group } = stage

  return {
    ...competitionData,
    competition: {
      ...competition,
      season: {
        ...season,
        stage: {
          ...stage,
          group: {
            ...group,
            event: eventUpdater(group.event),
          },
        },
      },
    },
  }
}

// Fetched from GET /events/{eventId}
export function competitionQueryOptions(
  eventId: Ref<number>,
): CompetitionQueryOptions {
  return queryOptions({
    queryKey: ['event', 'competition', eventId] as const,
    queryFn: async (): Promise<CompetitionData> => {
      const response = await sportsApiClient.Events.getById(eventId.value)

      return {
        competition: {
          ...response.api.data.competition,
          timestamp: {
            timestamp: response.api.timestamp,
            localSyncTime: Date.now(),
          },
        },
      }
    },
  })
}

export const useCompetitionQuery = (
  options?: Omit<CompetitionQueryOptions, 'queryKey' | 'queryFn'>,
): UseQueryReturnType<CompetitionData, Error> => {
  const eventId = useEventId()
  return useQuery<CompetitionData, Error, CompetitionData, CompetitionQueryKey>(
    {
      ...competitionQueryOptions(eventId),
      ...options,
    },
  )
}
